import React from 'react';
import LegalPageLayout from '../components/LegalPageLayout';

const privacySections = [
    {
        id: 'introduction',
        title: '1. Introduction',
        content: (
            <>
                <p>This Privacy Policy explains how Codemine ("we," "us," or "our") collects, uses, and discloses information about you when you use our website, web application, mobile applications, and services (collectively, the "Service"). We are committed to protecting your privacy.</p>
                <p className="mt-2"><strong>Last Updated:</strong> October 13, 2025</p>
            </>
        )
    },
    {
        id: 'info-we-collect',
        title: '2. Information We Collect',
        content: (
            <>
                <p><strong>a. Information You Provide to Us:</strong></p>
                <ul>
                    <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and authentication credentials. We use secure OAuth authentication and do not store passwords ourselves.</li>
                    <li><strong>Payment Information:</strong> When you subscribe to a paid plan (Starter, Creator, Pro, Business, or Mobile Deployment plans), our payment processor collects your payment card information. We do not store sensitive payment details ourselves.</li>
                    <li><strong>Subscription Data:</strong> We store your selected plan, token usage (daily, monthly, and lifetime), subscription status, and mobile deployment plan (if applicable).</li>
                    <li><strong>Inputs and Outputs:</strong> We collect the text prompts you submit ("Inputs") and the code, applications, and mobile apps generated by our AI ("Outputs") to provide the Service.</li>
                    <li><strong>Project Data:</strong> We store your generated projects, including file contents, project names, types (website, mobile app, game), and metadata in secure cloud storage.</li>
                    <li><strong>Mobile App Metadata:</strong> If you use our Mobile Deployment service, we collect app names, bundle IDs, descriptions, keywords, categories, privacy policy URLs, support URLs, and app store assets.</li>
                    <li><strong>Developer Credentials (BYODA):</strong> If you choose "Your Account Deploy" for mobile deployment, we securely store encrypted Apple Developer and Google Play credentials (API keys, team IDs, service account JSONs) to deploy apps on your behalf.</li>
                    <li><strong>Communications:</strong> We collect information you provide when you contact us for support or with questions.</li>
                </ul>
                <p><strong>b. Information We Collect Automatically:</strong></p>
                <ul>
                    <li><strong>Log and Usage Data:</strong> We log technical data about your use of the Service, including your IP address, browser type, operating system, pages visited, and the dates/times of your visits.</li>
                    <li><strong>Token Usage Tracking:</strong> We automatically track your AI token consumption (input and output tokens) for billing and plan limit enforcement.</li>
                    <li><strong>Cookies and Local Storage:</strong> We use cookies, local storage, and service workers to help operate our Service, remember your settings, enable PWA functionality, and understand how visitors use the Service.</li>
                    <li><strong>PWA Data:</strong> If you install Codemine as a Progressive Web App, we cache static assets locally on your device for offline access. This data is stored on your device and not transmitted to our servers.</li>
                </ul>
            </>
        )
    },
    {
        id: 'how-we-use-info',
        title: '3. How We Use Your Information',
        content: (
            <>
                <p>We use the information we collect to:</p>
                <ul>
                    <li>Provide, operate, and maintain our Service, including code generation, project storage, and mobile app deployment.</li>
                    <li>Process your payments and manage your subscription plans (base plans and mobile deployment add-ons).</li>
                    <li>Enforce token limits and track usage according to your plan (Starter: 50 daily, Creator: 500 daily, Pro: 2,000 daily, Business: 10,000 daily).</li>
                    <li>Generate AI-powered code, websites, mobile apps, and games using our AI engine.</li>
                    <li>Store your projects securely in encrypted cloud storage and databases.</li>
                    <li>Deploy your mobile applications to Apple App Store and Google Play Store (if you subscribe to Mobile Deployment plans).</li>
                    <li>Build native iOS and Android apps using industry-standard tools and frameworks on your behalf.</li>
                    <li>Communicate with you, including responding to your comments, questions, and providing customer service.</li>
                    <li>Send you deployment status updates, app review notifications, and service-related announcements.</li>
                    <li>Monitor and analyze trends, usage, and activities in connection with our Service.</li>
                    <li>Detect and prevent fraudulent transactions, abuse, and other illegal activities, and protect the rights and property of Codemine and others.</li>
                    <li>Improve our AI models, algorithms, and Service features.</li>
                </ul>
            </>
        )
    },
    {
        id: 'ai-model-training',
        title: '4. AI Model Training and Data Usage',
        content: (
            <>
                <p>We use AI technology to power our code generation features. When you submit a prompt, our AI engine processes it to generate code:</p>
                <ul>
                    <li><strong>AI Processing:</strong> Your prompts and generated code are processed by our AI systems to provide the Service.</li>
                    <li><strong>Token Tracking:</strong> We track token usage metadata (input and output tokens) and use this data solely for billing and plan limit enforcement.</li>
                    <li><strong>Service Improvement:</strong> To improve the accuracy, capability, and safety of our Service, we may analyze anonymized and aggregated usage patterns. We do not use your private code or sensitive information without your consent.</li>
                </ul>
                <p className="mt-2">You can opt out of having your data used for service improvement by contacting our support team, though this may limit our ability to troubleshoot issues specific to your generations.</p>
            </>
        )
    },
    {
        id: 'data-sharing',
        title: '5. Information Sharing and Disclosure',
        content: (
            <>
                <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
                <ul>
                    <li><strong>With Service Providers:</strong> We use essential service providers to operate our platform, including:
                        <ul className="ml-6 mt-2">
                            <li>Cloud infrastructure and hosting providers</li>
                            <li>Database and storage services</li>
                            <li>Payment processors for subscription billing</li>
                            <li>Authentication services</li>
                            <li>Mobile app build and compilation services</li>
                        </ul>
                        These providers are contractually bound to protect your data and use it only for providing services to us.
                    </li>
                    <li><strong>With App Stores:</strong> If you use Mobile Deployment services, we submit your app metadata and builds to Apple App Store Connect and Google Play Console on your behalf.</li>
                    <li><strong>Your Developer Accounts (BYODA):</strong> If you use "Your Account Deploy," we use your provided credentials to deploy apps under YOUR Apple Developer and Google Play accounts. You retain full ownership.</li>
                    <li><strong>For Legal Reasons:</strong> We may disclose your information if we believe that disclosure is in accordance with, or required by, any applicable law, regulation, legal process, or governmental request.</li>
                    <li><strong>Business Transfers:</strong> We may share information in connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business by another company.</li>
                </ul>
            </>
        )
    },
    {
        id: 'data-storage',
        title: '6. Data Storage and Security',
        content: (
            <>
                <p>We take data security seriously and employ industry-standard measures to protect your information:</p>
                <ul>
                    <li><strong>Encryption:</strong> All data transmitted between your browser and our servers is encrypted using HTTPS/TLS.</li>
                    <li><strong>Database Security:</strong> User profiles and project metadata are stored in secure databases with row-level security policies and access controls.</li>
                    <li><strong>File Storage:</strong> Project files are stored in secure cloud object storage with encryption and access controls.</li>
                    <li><strong>Credential Encryption:</strong> Developer credentials (for BYODA) are encrypted at rest using industry-standard encryption algorithms.</li>
                    <li><strong>Authentication:</strong> We use secure OAuth 2.0 and OpenID Connect authentication standards.</li>
                    <li><strong>Token Storage:</strong> Authentication tokens are stored in memory (not localStorage) for enhanced security.</li>
                    <li><strong>Service Worker Cache:</strong> PWA assets cached locally on your device are publicly available static files (HTML, CSS, JS) and contain no sensitive data.</li>
                </ul>
                <p className="mt-2">Despite our security measures, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.</p>
            </>
        )
    },
    {
        id: 'data-retention',
        title: '7. Data Retention Policy',
        content: (
            <>
                <p>We retain your personal information for as long as is necessary to provide the Service to you and to comply with our legal obligations:</p>
                <ul>
                    <li><strong>Account Data:</strong> Retained while your account is active.</li>
                    <li><strong>Project Data:</strong> Your generated projects (code, files) are retained in secure cloud storage indefinitely while your account is active to ensure you can access your project history.</li>
                    <li><strong>Token Usage Data:</strong> Daily token usage resets every 24 hours. Monthly usage resets every 30 days. Lifetime usage is retained permanently for billing records.</li>
                    <li><strong>Mobile Deployment Data:</strong> App submissions, build logs, and deployment history are retained for 12 months after app deployment.</li>
                    <li><strong>Developer Credentials:</strong> Encrypted credentials for BYODA are retained while your mobile deployment subscription is active.</li>
                    <li><strong>Backup Data:</strong> We maintain backups for disaster recovery purposes, retained for up to 90 days.</li>
                </ul>
                <p className="mt-2">If you delete your account, we will delete your personal information, project data, and credentials within 90 days, unless we are required to retain it for legal, regulatory, or compliance purposes.</p>
            </>
        )
    },
    {
        id: 'mobile-deployment',
        title: '8. Mobile Deployment Services',
        content: (
            <>
                <p>If you subscribe to our Mobile Deployment plans (PKR4,999/month or PKR7,999/month), additional data collection and processing occurs:</p>
                <p><strong>a. Codemine Deploy (Managed Service):</strong></p>
                <ul>
                    <li>We deploy your app under Codemine's Apple Developer and Google Play Developer accounts.</li>
                    <li>Your app is listed as "by Codemine Apps" on app stores.</li>
                    <li>We retain app metadata, build logs, and app store submission records.</li>
                    <li>Your app includes Codemine branding (splash screen).</li>
                    <li>Active subscription is required - canceling may result in app removal from stores.</li>
                </ul>
                <p className="mt-2"><strong>b. Your Account Deploy (BYODA):</strong></p>
                <ul>
                    <li>You provide your Apple Developer and Google Play credentials.</li>
                    <li>We securely store encrypted credentials to deploy apps on your behalf.</li>
                    <li>You own the app 100% - it's published under YOUR developer account.</li>
                    <li>You are responsible for Apple/Google developer account fees and compliance.</li>
                    <li>We use industry-standard build tools to compile native iOS/Android binaries from your web code.</li>
                    <li>Your app includes Codemine branding (splash screen) unless removed via premium upgrade.</li>
                </ul>
                <p className="mt-2"><strong>c. App Store Data:</strong></p>
                <ul>
                    <li>Apple and Google collect their own analytics, crash reports, and usage data according to their respective privacy policies.</li>
                    <li>We do not have access to end-user data from your deployed apps.</li>
                    <li>Push notifications (if enabled) are handled via secure cloud messaging services.</li>
                </ul>
            </>
        )
    },
    {
        id: 'pwa-offline',
        title: '9. Progressive Web App (PWA) and Offline Features',
        content: (
            <>
                <p>Codemine can be installed as a Progressive Web App on your device:</p>
                <ul>
                    <li><strong>Service Worker:</strong> We use a service worker to cache static assets (HTML, CSS, JavaScript) for offline access.</li>
                    <li><strong>Cached Data:</strong> Cached files are stored locally on your device and do not contain personal information.</li>
                    <li><strong>Manifest:</strong> Our web app manifest enables installation and provides app-like experience.</li>
                    <li><strong>Offline Mode:</strong> You can view cached pages offline, but code generation requires an internet connection.</li>
                    <li><strong>Push Notifications:</strong> Future versions may support push notifications with your explicit permission.</li>
                </ul>
            </>
        )
    },
    {
        id: 'your-rights',
        title: '10. Your Data Protection Rights',
        content: (
            <>
                <p>Depending on your location, you may have certain rights regarding your personal information:</p>
                <ul>
                    <li><strong>Access:</strong> You can request a copy of the personal information we hold about you.</li>
                    <li><strong>Correction:</strong> You can update or correct your account information in your dashboard.</li>
                    <li><strong>Deletion:</strong> You can request deletion of your account and associated data.</li>
                    <li><strong>Data Portability:</strong> You can export your project files at any time from the dashboard.</li>
                    <li><strong>Restriction:</strong> You can request that we restrict processing of your data.</li>
                    <li><strong>Objection:</strong> You can object to our processing of your data for certain purposes.</li>
                    <li><strong>Withdrawal of Consent:</strong> You can withdraw consent for data processing where we rely on consent.</li>
                </ul>
                <p className="mt-2">To exercise these rights, please contact us using the contact information below. We will respond within 30 days.</p>
            </>
        )
    },
    {
        id: 'international',
        title: '11. International Data Transfers',
        content: (
            <p>Codemine operates globally. Your information may be transferred to and processed in countries other than your country of residence, including the United States and Europe. We ensure appropriate safeguards are in place to protect your data when it is transferred internationally, including standard contractual clauses and compliance with GDPR, CCPA, and other applicable regulations.</p>
        )
    },
    {
        id: 'children',
        title: '12. Children\'s Privacy',
        content: (
            <p>Our Service is not directed to children under 13 (or 16 in the European Union). We do not knowingly collect personal information from children. If we learn we have collected information from a child under these ages, we will delete it immediately. If you believe a child has provided us with personal information, please contact us.</p>
        )
    },
    {
        id: 'changes',
        title: '13. Changes to This Policy',
        content: (
            <p>We may change this Privacy Policy from time to time. If we make material changes, we will notify you by revising the date at the top of the policy and, in some cases, we may provide you with additional notice (such as adding a statement to our homepage, sending you an email notification, or displaying a prominent notice in the app).</p>
        )
    },
    {
        id: 'contact',
        title: '14. Contact Us',
        content: (
            <>
                <p>If you have any questions about this Privacy Policy, please contact us at:</p>
                <ul className="mt-2">
                    <li><strong>Email:</strong> <a href="mailto:hellocodemine@yahoo.com" className="text-blue-500 hover:underline">hellocodemine@yahoo.com</a></li>
                    <li><strong>Website:</strong> <a href="https://www.codemineapp.com" className="text-blue-500 hover:underline">www.codemineapp.com</a></li>
                </ul>
            </>
        )
    }
];


const PrivacyPolicyPage: React.FC = () => {
    return (
        <LegalPageLayout
            pageTitle="Privacy Policy"
            sections={privacySections}
        />
    );
};

export default PrivacyPolicyPage;
